import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormArray,
  AbstractControl,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { ZoneService } from 'src/app/services/modules/instant-rate-manager/zone.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';

@Component({
  selector: 'app-create-rate-modal',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NgSelectModule],
  templateUrl: './create-rate-modal.component.html',
  styleUrl: './create-rate-modal.component.scss',
})
export class CreateRateModalComponent implements OnInit {
  // --- INJECTIONS ---
  private modalService = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private rateManagerService = inject(RateManagerService);
  private zoneService = inject(ZoneService);

  // --- STATE: FORMS ---
  rateForm!: FormGroup;
  isSubmitting = false;
  submitted = false;
  currentStep: number = 1;
  isSaveButtonEnabled: boolean = false;

  // --- STATE: CATEGORIES ---
  categoryTypeArray: Array<any> = [];
  selectedCategoryID: number | null = null;
  selectedCategory: string = '';

  // --- STATE: FULL TRUCK LOAD ---
  availableRateTypes: Array<{ id: number; name: string; label: string }> = [];
  ftlRowOptions: Array<Array<{ id: number; name: string; label: string }>> = [];
  vehicleList: Array<{ id: number; name: string }> = [];

  // --- STATE: LOCATIONS (Separated Search) ---
  originArray: Array<any> = [];
  destinationArray: Array<any> = [];
  isLoadingOrigin = false;
  isLoadingDestination = false;

  originSearchTerm$ = new Subject<string>();
  destinationSearchTerm$ = new Subject<string>();

  locationTypeArray = [
    { value: 'door', label: 'Door' },
    { value: 'depot', label: 'Depot' },
  ];

  // --- STATE: DYNAMIC FIELDS ---
  isLoadingDynamicFields = false;

  // --- LIFECYCLE ---
  ngOnInit() {
    this.rateForm = this.fb.group({
      rate_category_id: ['', Validators.required],
      rate_category_name: ['', Validators.required],
      origin_id: ['', Validators.required],
      origin_type: ['', Validators.required],
      pickup_type: ['', Validators.required],
      destination_id: ['', Validators.required],
      destination_type: ['', Validators.required],
      delivery_type: ['', Validators.required],
      transit_days: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],

      bracket_fields: this.fb.array([]),
      rates: this.fb.array([]),
    });

    this.setupOriginSearch();
    this.setupDestinationSearch();
    this.getCategory();
  }

  // --- SEARCH LOGIC (Separate Streams) ---
  private setupOriginSearch() {
    this.originSearchTerm$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => (this.isLoadingOrigin = true)),
      switchMap(term => from(this.getLocations(term)))
    ).subscribe(data => {
      this.originArray = data;
      this.isLoadingOrigin = false;
    });
  }

  private setupDestinationSearch() {
    this.destinationSearchTerm$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      tap(() => (this.isLoadingDestination = true)),
      switchMap(term => from(this.getLocations(term)))
    ).subscribe(data => {
      this.destinationArray = data;
      this.isLoadingDestination = false;
    });
  }

  async getLocations(term: string) {
    try {
      const payload = { name: term, page_size: 50 };
      const response = await this.zoneService.getProviderZones(payload);
      return (response.result || []).map((item: any) => ({
        value: item.id,
        label: item.name,
        type: item.type || 'door'
      }));
    } catch (error: any) {
      return [];
    }
  }

  // --- DATA FETCHING ---
  async getCategory() {
    try {
      const response = await this.rateManagerService.getProviderRatesCategories();
      this.categoryTypeArray = response.data.map((item: any) => ({
        value: item.id,
        label: item.name ? item.name.charAt(0).toUpperCase() + item.name.slice(1) : item.name,
        backend_name: item.backend_name,
        description: item.description,
        meta: item.meta,
        bracket_type: item.bracket_type,
      }));
    } catch (error: any) {
      this.categoryTypeArray = [];
    }
  }

  // --- EVENT HANDLERS ---
  onOriginSelected(item: any) {
    if (item) this.rateForm.patchValue({ origin_type: 'zone' });
  }

  onDestinationSelected(item: any) {
    if (item) this.rateForm.patchValue({ destination_type: 'zone'});
  }

  onChangeCategory = async (category: any) => {
    this.selectedCategoryID = category.value;
    this.selectedCategory = category.label;
    this.rateForm.patchValue({
      rate_category_id: category.value,
      rate_category_name: category.backend_name,
    });
  };

  get isFtl(): boolean {
    return this.selectedCategory.toLowerCase() === 'full truck load';
  }

  // --- STATE: METADATA ---
  hasConfiguredRanges = false;
  configuredRanges: any[] = [];

  // --- WIZARD NAVIGATION ---
  goToPrevStep = () => {
    if (this.currentStep > 1) {
      this.resetDynamicFields();
      this.resetBracketFields();
      this.resetRateFields();
      this.resetStepFields();
      this.submitted = false; // Reset validation state
      this.currentStep--;
      this.isSaveButtonEnabled = this.currentStep > 1;
    }
  };

  goToNextStep = async () => {
    this.resetDynamicFields();
    this.resetBracketFields();
    this.resetRateFields();
    this.resetStepFields();
    this.hasConfiguredRanges = false;
    this.configuredRanges = [];

    if (this.currentStep === 1) {
      if (!this.selectedCategoryID) return;

      this.isLoadingDynamicFields = true;
      const initLocs = await this.getLocations('');
      this.originArray = [...initLocs];
      this.destinationArray = [...initLocs];

      if (this.selectedCategory.toLowerCase() === 'full truck load' || this.selectedCategory.toLowerCase() === 'vehicle' || this.selectedCategory.toLowerCase() === 'car') {
        await this.initializeRateFields();
      } else {
        this.initializeBracketFields();
      }
      this.isLoadingDynamicFields = false;
      this.isSaveButtonEnabled = true;
    }

    this.currentStep++;
  };

  resetBracketFields() {
    (this.rateForm.get('bracket_fields') as FormArray).clear();
  }

  get bracketFields(): FormGroup[] {
    return (this.rateForm.get('bracket_fields') as FormArray).controls as FormGroup[];
  }

  initializeBracketFields() {
    const category = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID);
    const meta = category?.meta;

    let ranges: any[] = [];

    // Prioritize meta.bracket_ranges.ranges
    if (Array.isArray(meta?.bracket_ranges?.ranges) && meta.bracket_ranges.ranges.length > 0) {
      ranges = meta.bracket_ranges.ranges;
    } else if (meta?.settings) {
      // Fallback to searching settings for anything that looks like weight/volume/distance/quantity ranges
      const rangeSetting = meta.settings.find((s: any) => 
        s.key === 'weight_ranges' || 
        s.key === 'volume_ranges' || 
        s.key === 'distance_ranges' || 
        s.key === 'quantity_ranges' ||
        s.attribute_code === 'weight_ranges'
      );
      
      if (rangeSetting) {
        if (Array.isArray(rangeSetting.value)) {
          ranges = rangeSetting.value;
        } else if (typeof rangeSetting.value === 'string') {
          try {
            ranges = JSON.parse(rangeSetting.value);
          } catch (e: any) { console.error('Failed to parse ranges', e); }
        }
      }
    }

    if (ranges.length > 0) {
      this.hasConfiguredRanges = true;
      this.configuredRanges = ranges;
      ranges.forEach(range => this.addBracketField(range));
    } else {
      this.hasConfiguredRanges = false;
      this.configuredRanges = [];
      this.addBracketField({ min: 0, max: 1000, unit: '' });
    }
  }

  addBracketField(range?: { min: number, max: number, unit?: string }) {
    const bracketArray = this.rateForm.get('bracket_fields') as FormArray;

    if (this.hasConfiguredRanges && !range) {
      if (bracketArray.length >= this.configuredRanges.length) return;
      range = this.configuredRanges[bracketArray.length];
    }

    const isReadOnly = this.hasConfiguredRanges;

    const group = this.fb.group({
      range_from: [{ value: range?.min ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      range_to: [{ value: range?.max ?? '', disabled: isReadOnly }, [Validators.required, Validators.min(0), Validators.pattern(/^\d+$/)]],
      bracket_rate: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      unit: [{ value: range?.unit ?? '', disabled: true }],
    });

    bracketArray.push(group);
  }

  removeBracketField(index: number) {
    const bracketArray = this.rateForm.get('bracket_fields') as FormArray;
    if (bracketArray.length > 1) {
      bracketArray.removeAt(index);
    }
  }

  // --- FORM HELPERS ---
  resetStepFields() {
    this.rateForm.patchValue({
      origin_id: null, origin_type: null, pickup_type: null,
      destination_id: null, destination_type: null, delivery_type: null,
      transit_days: null,
    });
    Object.values(this.rateForm.controls).forEach(control => {
      control.markAsPristine();
      control.markAsUntouched();
    });
  }

  resetDynamicFields() {
    // no-op: now uses bracket_fields or ftl_fields
  }

  // --- RATE FIELDS (FTL / Vehicle) ---
  async initializeRateFields() {
    const [subCatResponse, vehicleResponse]: [any, any] = await Promise.all([
      this.rateManagerService.getProviderRatesSubCategories(this.selectedCategoryID!),
      this.isFtl ? this.rateManagerService.getVehicles().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    this.availableRateTypes = (subCatResponse.data || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      label: this.convertToHuman(item.name),
    }));
    const vehicleData = Array.isArray(vehicleResponse) ? vehicleResponse : (vehicleResponse?.data || []);
    this.vehicleList = vehicleData.map((item: any) => ({
      id: item.id,
      name: item.name,
    }));
    this.addRateField();
  }

  get rateFields(): FormGroup[] {
    return (this.rateForm.get('rates') as FormArray).controls as FormGroup[];
  }

  refreshRateRowOptions() {
    this.ftlRowOptions = this.rateFields.map((field, index) => {
      const currentId = field.get('rate_type_id')?.value;
      const usedIds = this.rateFields
        .map((f, i) => i !== index ? f.get('rate_type_id')?.value : null)
        .filter(Boolean);
      return this.availableRateTypes.filter(rt => !usedIds.includes(rt.id) || rt.id === currentId);
    });
  }

  get unusedRateTypes(): Array<{ id: number; name: string; label: string }> {
    const usedIds = this.rateFields.map(f => f.get('rate_type_id')?.value).filter(Boolean);
    return this.availableRateTypes.filter(rt => !usedIds.includes(rt.id));
  }

  onRateTypeChange() {
    this.refreshRateRowOptions();
  }

  addRateField() {
    const rateArray = this.rateForm.get('rates') as FormArray;
    if (rateArray.length >= this.availableRateTypes.length) return;

    const group = this.fb.group({
      rate_type_id: [null, Validators.required],
      external_vehicle_id: [null],
      rate: ['', [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
    });
    rateArray.push(group);
    this.refreshRateRowOptions();
  }

  removeRateField(index: number) {
    const rateArray = this.rateForm.get('rates') as FormArray;
    if (rateArray.length > 1) {
      rateArray.removeAt(index);
      this.refreshRateRowOptions();
    }
  }

  resetRateFields() {
    (this.rateForm.get('rates') as FormArray).clear();
    this.availableRateTypes = [];
    this.ftlRowOptions = [];
    this.vehicleList = [];
  }

  // --- SUBMISSION ---
  handleSubmitRate = () => {
    this.submitted = true;
    if (this.rateForm.invalid) {
      this.rateForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    const categoryName = this.selectedCategory.toLowerCase();

    if (categoryName === 'full truck load' || categoryName === 'vehicle' || categoryName === 'car') {
      this.handleCreateFtlRate();
    } else {
      this.handleCreateBracketRate();
    }
  };

  handleCreateBracketRate() {
    const { bracket_fields, rates, ...formData } = this.rateForm.getRawValue();
    const bracket_type = this.categoryTypeArray.find(c => c.value === this.selectedCategoryID)?.bracket_type;
    const rate_brackets = bracket_fields.map((field: any, index: number) => ({
      bracket_order: index + 1,
      range_from: +field.range_from,
      range_to: +field.range_to,
      bracket_rate: parseFloat(field.bracket_rate),
      bracket_type,
    }));
    this.rateManagerService.createRate({ ...formData, rate_brackets, status: 'in_review' }).subscribe({
      next: () => this.handleSuccess(),
      error: (err) => {
        AlertUtil.error(err?.error?.message || 'Failed to create rate.');
        this.isSubmitting = false;
      }
    });
  }

  handleCreateCarRate() {
    this.handleCreateFtlRate();
  }

  handleCreatePalletRate() {
    this.handleCreateBracketRate();
  }

  handleCreateFurnitureRate() {
    this.handleCreateBracketRate();
  }

  handleCreatePackageRate() {
    this.handleCreateBracketRate();
  }

  handleCreateFtlRate() {
    const { bracket_fields, rates: rawRates, ...formData } = this.rateForm.getRawValue();
    const isFtl = this.isFtl;
    const ratesData = rawRates.map((field: any) => {
      const rateObj: any = {
        rate: field.rate?.toString() || '0',
        rate_type_id: +field.rate_type_id,
      };
      if (isFtl) {
        rateObj.external_vehicle_id = field.external_vehicle_id ? +field.external_vehicle_id : null;
      }
      return rateObj;
    });
    this.rateManagerService.createRate({ ...formData, rates: ratesData, status: 'in_review' }).subscribe({
      next: () => this.handleSuccess(),
      error: (err) => {
        AlertUtil.error(err?.error?.message || 'Failed to create rate.');
        this.isSubmitting = false;
      }
    });
  }

  private handleSuccess() {
    AlertUtil.success('Rate created successfully.');
    this.closeModal('success');
    this.isSubmitting = false;
  }

  // --- UTIL ---
  convertToHuman = (text: string) => text.trim().replace(/\s+/g, ' ').split(/[_\s]/).filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  closeModal = (result?: string) => {
    this.submitted = false; // Reset validation state
    this.modalService.close(result);
  };

  cancelModal() {
    this.submitted = false; // Reset validation state
    this.modalService.dismiss();
  }

  allowNumbersWithDecimal(event: KeyboardEvent, field: AbstractControl, allowDecimal: boolean) {
    const input = field.value?.toString() || '';
    const char = event.key;
    if (!/[0-9.]/.test(char) || (char === '.' && (!allowDecimal || input.includes('.')))) {
      event.preventDefault();
    }
  }
}
